import React, {
  FC,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { MessageVolumeResponse, MessageVolumeBucket } from '@dreamai/types';
import api from '../../api';
import OnboardingOverviewPanel from './OnboardingOverviewPanel';
import SignupFunnelPanel from './SignupFunnelPanel';
import './activation-dashboard.scss';

/**
 * Onboarding tab of Platform Stats.
 *
 * Panels, in narrative order:
 *   0. 7-day retention, headline numbers and the onboarding funnel
 *      (OnboardingOverviewPanel), filtered by source and onboarding variant
 *   1. Daily sign-ups by referrer type (SignupFunnelPanel)
 *   2. Drop-off re-engagement
 *   3. Activation messages (Day 1/2/4)
 *   4. Daily message volume per user, before/after completing onboarding
 */

/** GET /activation-stats/onboarding-filters — built from the data. */
interface FilterOption {
  key: string;
  label: string;
  count: number;
  group?: 'referral' | 'campaign' | 'other';
}

/** A campaign code; offered once its source is ticked. */
interface CodeOption {
  /** acquisition_codes.id */
  key: string;
  /** Campaign name, e.g. "Meta Ads: PA". */
  label: string;
  /** Without the DREAM- prefix. */
  code: string;
  /** Source key, e.g. "campaign:meta_ads_pa". */
  source: string;
  count: number;
}

interface OnboardingFiltersResponse {
  success: boolean;
  sources: FilterOption[];
  codes: CodeOption[];
  variants: FilterOption[];
}

const SOURCE_PHRASES: Record<string, string> = {
  dream_referral: 'users referred by a Dream team member',
  personal_referral: 'users referred by another Dream user',
  organisation: 'users invited by their organisation',
  unattributed: 'users with no recorded source',
  'referrer:ugc_creator': 'users referred by a UGC creator',
};

const VARIANT_PHRASES: Record<string, string> = {
  '': 'all onboarding methods',
  skateboard: 'the Skateboard onboarding',
  walkthrough: "Ben's walkthrough",
  chat: 'chat-only onboarding (before the web setup page)',
};

const describeSource = (
  source: string,
  filters: OnboardingFiltersResponse | null,
) => {
  const label = filters?.sources.find((s) => s.key === source)?.label ?? source;
  return (
    SOURCE_PHRASES[source] ??
    (source.startsWith('campaign:')
      ? `users who signed up with the ${label} campaign code`
      : source.startsWith('referrer:')
        ? `users referred by a ${label} user`
        : `${label} users`)
  );
};

/** "Showing <who> on <which onboarding>", built from the filter dropdowns. */
const describeCohort = (
  sources: string[],
  codes: string[],
  variant: string,
  filters: OnboardingFiltersResponse | null,
) => {
  const variantOption = filters?.variants.find((v) => v.key === variant);
  const pickedCodes = (filters?.codes ?? []).filter((c) =>
    codes.includes(c.key),
  );
  // A source narrowed to specific codes is described by those codes.
  const sourcePhrases = sources.flatMap((source) => {
    const narrowed = pickedCodes.filter((c) => c.source === source);
    return narrowed.length > 0
      ? narrowed.map(
          (c) =>
            `users who signed up with the ${c.label} code (DREAM-${c.code})`,
        )
      : [describeSource(source, filters)];
  });
  // "users referred by X or invited by their organisation": later phrases
  // drop their leading "users".
  const phrases = sourcePhrases.map((phrase, i) =>
    i > 0 ? phrase.replace(/^users /, '') : phrase,
  );
  const who =
    phrases.length === 0
      ? 'all users'
      : phrases.length === 1
        ? phrases[0]
        : `${phrases.slice(0, -1).join(', ')} or ${phrases[phrases.length - 1]}`;
  const how =
    VARIANT_PHRASES[variant] ??
    `the ${variantOption?.label ?? variant} onboarding`;
  return `Showing ${who} on ${how}`;
};

const SOURCE_GROUP_LABELS: Record<string, string> = {
  referral: 'Referrals',
  campaign: 'Campaign codes',
  other: 'Other',
};

/**
 * Tick-box picker for sources or campaign codes (reuses the cost-filter
 * dropdown styles). Nothing ticked means all.
 */
const TickBoxFilter: FC<{
  groups: [string, FilterOption[]][];
  groupLabels: Record<string, string>;
  selected: string[];
  onChange: (selected: string[]) => void;
  /** Plural noun, e.g. "sources" → "All sources" / "2 sources". */
  noun: string;
}> = ({ groups, groupLabels, selected, onChange, noun }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  const labels = new Map(
    groups.flatMap(([, options]) => options.map((o) => [o.key, o.label])),
  );
  const summary =
    selected.length === 0
      ? `All ${noun}`
      : selected.length === 1
        ? (labels.get(selected[0]) ?? selected[0])
        : `${selected.length} ${noun}`;
  const toggle = (key: string) =>
    onChange(
      selected.includes(key)
        ? selected.filter((k) => k !== key)
        : [...selected, key],
    );

  return (
    <div
      className={`cost-filter source-filter${selected.length ? ' filtered' : ''}`}
      ref={rootRef}
    >
      <button
        type='button'
        className='cost-filter-toggle'
        aria-haspopup='listbox'
        aria-expanded={open}
        aria-label={`Filter by ${noun}`}
        onClick={() => setOpen((o) => !o)}
      >
        <span className='filter-summary'>{summary}</span>
        <span className='filter-caret' aria-hidden='true'>
          ▾
        </span>
      </button>
      {open && (
        <div className='cost-filter-menu' role='listbox' aria-multiselectable>
          <div className='filter-menu-actions'>
            <button
              type='button'
              disabled={selected.length === 0}
              onClick={() => onChange([])}
            >
              All {noun}
            </button>
          </div>
          {groups.map(([group, options]) => (
            <div key={group} className='source-filter-group'>
              <div className='source-filter-group-label'>
                {groupLabels[group] ?? group}
              </div>
              {options.map((o) => (
                <label
                  key={o.key}
                  className='filter-menu-option'
                  role='option'
                  aria-selected={selected.includes(o.key)}
                >
                  <input
                    type='checkbox'
                    checked={selected.includes(o.key)}
                    onChange={() => toggle(o.key)}
                  />
                  <span>
                    {o.label} ({o.count})
                  </span>
                </label>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const VOLUME_BUCKET_LABELS: Record<
  MessageVolumeBucket,
  { label: string; sub: string; tableLabel: string }
> = {
  pre: {
    label: 'Before completing',
    sub: 'invite → completed',
    tableLabel: 'Before completing (invite → completed)',
  },
  d1_7: {
    label: 'Days 1–7',
    sub: 'after completing',
    tableLabel: 'After completing · days 1–7',
  },
  d8_14: {
    label: 'Days 8–14',
    sub: 'after completing',
    tableLabel: 'After completing · days 8–14',
  },
  d15_30: {
    label: 'Days 15–30',
    sub: 'after completing',
    tableLabel: 'After completing · days 15–30',
  },
  d31_plus: {
    label: 'Day 31+',
    sub: 'after completing',
    tableLabel: 'After completing · day 31+',
  },
};

const pctFmt = (p: number) => `${Math.round(p * 100)}%`;

/** Native date inputs emit partial years (e.g. 0001-10-09) while typing. */
const isPlausibleDate = (value: string) =>
  /^\d{4}-\d{2}-\d{2}$/.test(value) && Number(value.slice(0, 4)) >= 2000;

const ukDate = (iso: string) =>
  iso
    ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`
    : 'dd/mm/yyyy';

/** Native date picker, but always shown as dd/mm/yyyy (Chrome follows the browser language otherwise). */
export const UkDateInput = ({
  value,
  min,
  max,
  onChange,
}: {
  value: string;
  min?: string;
  max?: string;
  onChange: (value: string) => void;
}) => (
  <span className='uk-date-input'>
    <input
      type='date'
      value={value}
      min={min}
      max={max}
      onClick={(e) => e.currentTarget.showPicker?.()}
      onChange={(e) =>
        isPlausibleDate(e.target.value) && onChange(e.target.value)
      }
    />
    <span className='uk-date-display' aria-hidden='true'>
      {ukDate(value)}
    </span>
  </span>
);

const isoDaysAgo = (days: number) =>
  new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

/** Quick ranges: the last N×24h before now, counted like the Scaling tab. */
const RANGE_DAYS = [30, 60, 90];

const fmtShortDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

/**
 * Response of GET /activation-stats/reengagement (DRE-274). Local until the
 * shape settles and gets promoted into @dreamai/types.
 */
interface ReengagementStatsResponse {
  success: boolean;
  nudges: {
    inviteNudge: number;
    inviteDeferralNudge: number;
    connectNudge: number;
    connectFinalNudge: number;
  };
  nudged: number;
  repliedAfterNudge: number;
  completedAfterNudge: number;
  states: { active: number; deferred: number; stopped: number };
  stoppedReasons: Array<{ reason: string; count: number }>;
  stuckFlagged: number;
  blockers: Array<{
    kind: string;
    category: string | null;
    provider: string | null;
    count: number;
    consentToRecontact: number;
  }>;
  error?: string;
}

interface ReengagementUser {
  id: string;
  name: string | null;
  companyName: string | null;
  state: string;
  stoppedReason: string | null;
  stuckFlaggedAt: string | null;
}

interface ReengagementUsersResponse {
  success: boolean;
  state: 'stuck' | 'deferred' | 'stopped';
  users: ReengagementUser[];
  error?: string;
}

const NUDGE_LABELS: Array<
  [keyof ReengagementStatsResponse['nudges'], string, string]
> = [
  ['inviteNudge', 'Invite nudge', '≥12h after drop-off, pre-connect'],
  ['inviteDeferralNudge', 'Deferral offer', '≥72h, "maybe later?"'],
  ['connectNudge', 'Connect reminder', '≥12h after connect link'],
  ['connectFinalNudge', 'Final connect reminder', '≥36h after connect link'],
];

/**
 * Which of a user's successful sends of a touch the panel counts: the first
 * one, the repeats after it (the touch's recurring form), or all of them.
 */
const TOUCH_SEND_FILTERS = [
  { key: 'first', label: 'First send' },
  { key: 'repeat', label: 'Repeat sends' },
  { key: 'all', label: 'All' },
] as const;
type TouchSendFilter = (typeof TOUCH_SEND_FILTERS)[number]['key'];

/**
 * Response of GET /activation-stats/touch-messages?dayIndex=N&sends=… — the
 * per-day panel off the activation_touches log.
 */
interface TouchMessagesResponse extends TouchMessageStats {
  success: boolean;
  dayIndex: number;
  touchType: string | null;
  sends: TouchSendFilter;
  completedFrom: string;
  completedTo: string;
  error?: string;
}

/** What the Activation messages panel renders. */
interface TouchMessageStats {
  days: Array<{
    day: string;
    completed: number;
    /** Users for whom activation day N has arrived; null when not measured. */
    reachedDay: number | null;
    briefingDelivered: number;
    /** Users with a counted send. */
    sent: number;
    /** Counted sends to them; exceeds `sent` once repeats are included. */
    sendCount: number;
  }>;
  options: Array<{ option: number; count: number }>;
  outcomes: Array<{ outcome: string; count: number }>;
  engagement: {
    touched: TouchEngagementCohort | null;
    untouched: TouchEngagementCohort | null;
  };
}

interface TouchEngagementCohort {
  users: number;
  replied1h: number;
  replied24h: number;
  taskWithin24h: number;
}

/**
 * Copy and labels for each activation-sequence touch. Days listed in
 * ACTIVATION_TOUCH_DAYS without an entry render with generic wording.
 */
interface ActivationTouchConfig {
  /** Tab label suffix, e.g. "cold read". */
  name: string;
  /** Panel heading after "Day N — ". */
  title: string;
  /** info-dot tooltip: what the touch is and where its numbers come from. */
  tooltip: string;
  /** Wording for the briefing the touch is scheduled after. */
  briefingLabel: string;
  /** Wording for the sent KPI and the recipients engagement row. */
  sentLabel: string;
  recipientsLabel: string;
  optionNames: Record<number, string>;
  /** Suffix after an option name in the option-mix KPIs. */
  optionSuffix: string;
}

const ACTIVATION_TOUCH_CONFIG: Record<number, ActivationTouchConfig> = {
  1: {
    name: 'cold read',
    title: 'cold read after the first briefing',
    tooltip:
      'The proactive suggestion sent ~2 minutes after a user’s first daily briefing, on the calendar day after onboarding; repeats are the weekly bookings nudge. Everything comes from the activation_touches log (recorded from its deploy onwards; test-mode sends excluded).',
    briefingLabel: 'got their first briefing',
    sentLabel: 'got the Day 1 cold read',
    recipientsLabel: 'Got the cold read',
    optionNames: { 1: 'School admin', 2: 'New venture', 3: 'Fallback' },
    optionSuffix: 'angle',
  },
  2: {
    name: 'weekend plan',
    title: 'weekend plan after the day-2 briefing',
    tooltip:
      'The tailored weekend recommendations sent an hour after the daily briefing on the second calendar day after onboarding (or day 3 if that one was missed); repeats are the weekly weekend plan. Everything comes from the activation_touches log; test-mode sends excluded.',
    briefingLabel: 'got a day-2 briefing',
    sentLabel: 'got the weekend plan',
    recipientsLabel: 'Got the weekend plan',
    optionNames: { 1: 'Family weekend', 2: 'Solo & friends' },
    optionSuffix: 'variant',
  },
  4: {
    name: 'briefing suggestions',
    title: 'briefing suggestions on the evening of day 4',
    tooltip:
      'Two evidence-gated suggestions for what to add to the daily briefing, sent at 19:00 local on the fourth day after onboarding; repeats are the monthly version on the last Monday of each month. Everything comes from the activation_touches log; test-mode sends excluded.',
    briefingLabel: 'got a day-4 briefing',
    sentLabel: 'got the briefing suggestions',
    recipientsLabel: 'Got the suggestions',
    optionNames: {},
    optionSuffix: 'variant',
  },
};

const activationTouchConfig = (dayIndex: number): ActivationTouchConfig =>
  ACTIVATION_TOUCH_CONFIG[dayIndex] ?? {
    name: 'message',
    title: `proactive message after the day-${dayIndex} briefing`,
    tooltip: `The Day ${dayIndex} activation message. Numbers come from the activation_touches log once its job ships; test-mode sends excluded.`,
    briefingLabel: `got a day-${dayIndex} briefing`,
    sentLabel: `got the Day ${dayIndex} message`,
    recipientsLabel: 'Got the message',
    optionNames: {},
    optionSuffix: 'variant',
  };

/** The finished report, carried in a `done` TouchSentimentJob. */
interface TouchSentimentResponse {
  success: boolean;
  dayIndex: number;
  touchType: string | null;
  completedFrom: string;
  completedTo: string;
  limit: number;
  generatedAt: string;
  sampling: TouchSampling;
  /** Every `sent` touch for the day in the date range (the population). */
  cohort: number;
  strata: { option: number | null; cohort: number; sampled: number }[];
  /** Sample-only counts — an estimate for the cohort, not a census. */
  totals: {
    recipients: number;
    analysed: number;
    noReply: number;
    unavailable: number;
    positive: number;
    neutral: number;
    negative: number;
    successful: number;
    harmful: number;
    /** Subset of harmful rated dangerous. */
    dangerous: number;
  };
  /** Cohort-wide harm screen over recipients outside the sample. */
  harmScan: {
    scanned: number;
    unscanned: number;
    flagged: number;
    confirmed: number;
    confirmedDangerous: number;
  };
  items: TouchSentimentItem[];
  error?: string;
}

interface TouchSentimentProgress {
  phase: 'sample' | 'harm_scan';
  completed: number;
  total: number;
  cohort: number;
}

/**
 * Job record from POST /activation-stats/touch-sentiment (202) and
 * GET /activation-stats/touch-sentiment/:jobId. The report runs longer than
 * a single request is allowed to, so the panel polls until it is done.
 */
interface TouchSentimentJob {
  jobId: string;
  status: 'running' | 'done' | 'failed';
  progress: TouchSentimentProgress | null;
  result: TouchSentimentResponse | null;
  error: string | null;
}

interface TouchSentimentJobEnvelope {
  success: boolean;
  job: TouchSentimentJob;
}

/** A backend that still answers synchronously returns the report itself. */
const toSentimentJob = (
  res: TouchSentimentJobEnvelope | TouchSentimentResponse,
): TouchSentimentJob =>
  'job' in res
    ? res.job
    : {
        jobId: '',
        status: 'done',
        progress: null,
        result: res,
        error: null,
      };

const SENTIMENT_POLL_MS = 2000;
/** Give up polling after this long — the server record itself expires later. */
const SENTIMENT_POLL_TIMEOUT_MS = 15 * 60 * 1000;

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const describeSentimentProgress = (p: TouchSentimentProgress | null) => {
  if (!p) return 'Starting…';
  if (p.phase === 'sample') {
    return `Analysing replies ${Math.min(p.completed, p.total)}/${p.total}…`;
  }
  return `Screening ${Math.min(p.completed, p.total)}/${p.total} for harm…`;
};

type TouchSampling = 'random' | 'recent';

// Fixed vocabularies mirrored from dream-be lib/activationTouchSentiment.ts.
// The analysis deliberately carries no free text about the user's reply.
const TOUCH_REACTION_LABELS: Record<string, string> = {
  delegated_task: 'Delegated a task to the secretary',
  asked_follow_up: 'Asked a follow-up question',
  engaged_conversation: 'Engaged in conversation',
  acknowledged: 'Acknowledged briefly',
  informational_reply: 'Replied with information only',
  declined: 'Declined',
  asked_to_stop: 'Asked not to be messaged',
  confused: 'Confused by the message',
  annoyed: 'Annoyed or dismissive',
  distressed: 'Upset or distressed',
  unclear: 'Reaction unclear',
};

const TOUCH_HARM_LABELS: Record<string, string> = {
  intrusive: 'Felt intrusive or creepy',
  inaccurate_guess: 'Inaccurate or embarrassing guess',
  insensitive: 'Touched something sensitive insensitively',
  opt_out_request: 'User asked to stop',
  anger_or_distress: 'User reacted with anger or distress',
  other: 'Other harm',
};

interface TouchSentimentItem {
  userId: string;
  dayIndex: number;
  touchType: string;
  option: number | null;
  sentAt: string;
  replyCount: number;
  source: 'sample' | 'harm_scan';
  analysis: {
    sentiment: 'positive' | 'neutral' | 'negative';
    successful: boolean;
    harmful: boolean;
    /** Fixed category from TOUCH_HARM_LABELS; never free text. */
    harmCategory: string | null;
    /** 'annoying' (trust dent) vs 'dangerous' (needs a human look); null unless harmful. */
    harmSeverity: 'annoying' | 'dangerous' | null;
    /** Fixed category from TOUCH_REACTION_LABELS; never free text. */
    reaction: string;
  } | null;
}

const SENTIMENT_LIMITS = [25, 50, 100, 200];

const SAMPLING_MODES: { value: TouchSampling; label: string }[] = [
  { value: 'random', label: 'Random (unbiased estimate)' },
  { value: 'recent', label: 'Most recent (what happened lately)' },
];

/** Activation-sequence touches: one panel tab and sentiment option each. */
const ACTIVATION_TOUCH_DAYS = [1, 2, 4];

/** Per-day option labels; days without an entry render "Option N". */
const touchOptionName = (dayIndex: number, option: number) =>
  activationTouchConfig(dayIndex).optionNames[option] ?? `Option ${option}`;

const TOUCH_OUTCOME_LABELS: Record<string, string> = {
  sent: 'Sent',
  skipped_no_session: 'Skipped — no completed session',
  skipped_missing_thread: 'Skipped — missing agent/thread',
  skipped_no_company: 'Skipped — user has no company',
  skipped_already_sent: 'Skipped — already sent',
  skipped_stale_onboarding: 'Skipped — onboarding too old',
  research_failed:
    'Research failed — specialist timed out / no results (nothing sent)',
  error: 'Error sending',
};

const ActivationDashboard: FC = () => {
  const { getAccessTokenSilently } = useAuth0();

  const [invitedFrom, setInvitedFrom] = useState(isoDaysAgo(30));
  const [invitedTo, setInvitedTo] = useState(isoDaysAgo(0));
  // Set by the quick-range buttons; editing a date box switches it off.
  const [rangeDays, setRangeDays] = useState<number | null>(30);
  const pickRange = (days: number) => {
    setRangeDays(days);
    setInvitedFrom(isoDaysAgo(days));
    setInvitedTo(isoDaysAgo(0));
  };
  const [sources, setSources] = useState<string[]>([]);
  const [codes, setCodes] = useState<string[]>([]);
  const [variant, setVariant] = useState('');
  const [filters, setFilters] = useState<OnboardingFiltersResponse | null>(
    null,
  );

  const [volume, setVolume] = useState<MessageVolumeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [reengagement, setReengagement] =
    useState<ReengagementStatsResponse | null>(null);

  const [reengagementUsersState, setReengagementUsersState] = useState<
    'stuck' | 'deferred' | 'stopped' | null
  >(null);
  const [reengagementUsers, setReengagementUsers] = useState<
    ReengagementUser[]
  >([]);
  const [reengagementUsersLoading, setReengagementUsersLoading] =
    useState(false);

  const [touchSummary, setTouchSummary] = useState<Array<
    [number, TouchMessagesResponse]
  > | null>(null);
  const [touchDay, setTouchDay] = useState(1);
  const [touchSends, setTouchSends] = useState<TouchSendFilter>('first');
  const [touchStats, setTouchStats] = useState<TouchMessagesResponse | null>(
    null,
  );
  const [touchStatsError, setTouchStatsError] = useState<string | null>(null);
  const [sentiment, setSentiment] = useState<TouchSentimentResponse | null>(
    null,
  );
  const sentimentDay = touchDay;
  const touchConfig = activationTouchConfig(touchDay);
  const [sentimentLimit, setSentimentLimit] = useState(50);
  const [sentimentSampling, setSentimentSampling] =
    useState<TouchSampling>('random');
  const [sentimentLoading, setSentimentLoading] = useState(false);
  const [sentimentProgress, setSentimentProgress] =
    useState<TouchSentimentProgress | null>(null);
  const [sentimentError, setSentimentError] = useState<string | null>(null);

  const authGet = useCallback(
    async <T,>(path: string): Promise<T> => {
      const token = await getAccessTokenSilently();
      const res = await api.get<T>(path, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data;
    },
    [getAccessTokenSilently],
  );

  const authPost = useCallback(
    async <T,>(path: string, body: unknown): Promise<T> => {
      const token = await getAccessTokenSilently();
      const res = await api.post<T>(path, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data;
    },
    [getAccessTokenSilently],
  );

  const sentimentRequestId = useRef(0);

  const runSentiment = useCallback(async () => {
    const requestId = ++sentimentRequestId.current;
    const isCurrent = () => requestId === sentimentRequestId.current;
    setSentiment(null);
    setSentimentLoading(true);
    setSentimentProgress(null);
    setSentimentError(null);
    try {
      const started = await authPost<
        TouchSentimentJobEnvelope | TouchSentimentResponse
      >('/activation-stats/touch-sentiment', {
        dayIndex: sentimentDay,
        completedFrom: invitedFrom,
        completedTo: invitedTo,
        limit: sentimentLimit,
        sampling: sentimentSampling,
      });
      let job = toSentimentJob(started);
      const deadline = Date.now() + SENTIMENT_POLL_TIMEOUT_MS;
      while (job.status === 'running') {
        if (Date.now() > deadline) {
          throw new Error('Analysis timed out');
        }
        await sleep(SENTIMENT_POLL_MS);
        if (!isCurrent()) return;
        const polled = await authGet<TouchSentimentJobEnvelope>(
          `/activation-stats/touch-sentiment/${job.jobId}`,
        );
        if (!isCurrent()) return;
        job = polled.job;
        setSentimentProgress(job.progress);
      }
      if (job.status === 'failed' || !job.result) {
        throw new Error(job.error ?? 'Analysis failed');
      }
      setSentiment(job.result);
    } catch (err) {
      if (!isCurrent()) return;
      console.error('Failed to analyse touch sentiment', err);
      setSentimentError(
        err instanceof Error && err.message === 'Analysis timed out'
          ? 'Analysis is taking too long — try a smaller sample.'
          : 'Analysis failed — try again.',
      );
    } finally {
      if (isCurrent()) {
        setSentimentLoading(false);
        setSentimentProgress(null);
      }
    }
  }, [
    authGet,
    authPost,
    invitedFrom,
    invitedTo,
    sentimentDay,
    sentimentLimit,
    sentimentSampling,
  ]);

  useEffect(() => {
    sentimentRequestId.current += 1;
    setSentiment(null);
    setSentimentError(null);
    setSentimentLoading(false);
    setSentimentProgress(null);
  }, [invitedFrom, invitedTo, sentimentDay, sentimentLimit, sentimentSampling]);

  const cohortParams = useMemo(() => {
    const params = new URLSearchParams({ invitedFrom, invitedTo });
    if (rangeDays) params.set('days', String(rangeDays));
    for (const source of sources) params.append('source', source);
    for (const code of codes) params.append('code', code);
    if (variant) params.set('variant', variant);
    return params.toString();
  }, [invitedFrom, invitedTo, rangeDays, sources, codes, variant]);

  useEffect(() => {
    (async () => {
      try {
        setFilters(
          await authGet<OnboardingFiltersResponse>(
            `/activation-stats/onboarding-filters?invitedFrom=${invitedFrom}&invitedTo=${invitedTo}${rangeDays ? `&days=${rangeDays}` : ''}`,
          ),
        );
      } catch {
        setError('Failed to load filters');
      }
    })();
  }, [authGet, invitedFrom, invitedTo, rangeDays]);

  const sourceGroups = useMemo(() => {
    const groups = new Map<string, FilterOption[]>();
    for (const o of filters?.sources ?? []) {
      const g = o.group ?? 'other';
      groups.set(g, [...(groups.get(g) ?? []), o]);
    }
    return Array.from(groups.entries());
  }, [filters]);

  // Codes of the ticked campaign sources, grouped by source.
  const codeGroups = useMemo(() => {
    const groups = new Map<string, FilterOption[]>();
    for (const c of filters?.codes ?? []) {
      if (!sources.includes(c.source)) continue;
      groups.set(c.source, [
        ...(groups.get(c.source) ?? []),
        { key: c.key, label: `${c.label} · DREAM-${c.code}`, count: c.count },
      ]);
    }
    return Array.from(groups.entries());
  }, [filters, sources]);
  const codeGroupLabels = useMemo(
    () =>
      Object.fromEntries((filters?.sources ?? []).map((o) => [o.key, o.label])),
    [filters],
  );
  const changeSources = (next: string[]) => {
    setSources(next);
    const keep = new Set(
      (filters?.codes ?? [])
        .filter((c) => next.includes(c.source))
        .map((c) => c.key),
    );
    setCodes((prev) => prev.filter((key) => keep.has(key)));
  };

  useEffect(() => {
    (async () => {
      try {
        setVolume(
          await authGet<MessageVolumeResponse>(
            `/activation-stats/message-volume?${cohortParams}`,
          ),
        );
      } catch {
        setError('Failed to load message volume');
      }
    })();
  }, [authGet, cohortParams]);

  useEffect(() => {
    (async () => {
      try {
        setReengagement(
          await authGet<ReengagementStatsResponse>(
            `/activation-stats/reengagement?${cohortParams}`,
          ),
        );
      } catch {
        setError('Failed to load re-engagement stats');
      }
    })();
  }, [authGet, cohortParams]);

  useEffect(() => {
    (async () => {
      if (!reengagementUsersState) {
        setReengagementUsers([]);
        return;
      }
      setReengagementUsersLoading(true);
      try {
        const data = await authGet<ReengagementUsersResponse>(
          `/activation-stats/reengagement/users?state=${reengagementUsersState}&${cohortParams}`,
        );
        if (data.success) setReengagementUsers(data.users);
      } catch {
        setError('Failed to load re-engagement users');
      } finally {
        setReengagementUsersLoading(false);
      }
    })();
  }, [authGet, cohortParams, reengagementUsersState]);

  const touchStatsRequestId = useRef(0);

  useEffect(() => {
    const requestId = ++touchStatsRequestId.current;
    setTouchStats(null);
    setTouchStatsError(null);
    (async () => {
      try {
        const stats = await authGet<TouchMessagesResponse>(
          `/activation-stats/touch-messages?dayIndex=${touchDay}&sends=${touchSends}&completedFrom=${invitedFrom}&completedTo=${invitedTo}`,
        );
        if (requestId !== touchStatsRequestId.current) return;
        setTouchStats(stats);
      } catch {
        if (requestId !== touchStatsRequestId.current) return;
        setTouchStatsError(`Failed to load Day ${touchDay} message stats`);
      }
    })();
  }, [authGet, invitedFrom, invitedTo, touchDay, touchSends]);

  useEffect(() => {
    let cancelled = false;
    setTouchSummary(null);
    Promise.all(
      ACTIVATION_TOUCH_DAYS.map(async (d) => {
        const stats = await authGet<TouchMessagesResponse>(
          `/activation-stats/touch-messages?dayIndex=${d}&sends=first&completedFrom=${invitedFrom}&completedTo=${invitedTo}`,
        );
        return [d, stats] as [number, TouchMessagesResponse];
      }),
    )
      .then((rows) => !cancelled && setTouchSummary(rows))
      .catch(
        () => !cancelled && setError('Failed to load activation messages'),
      );
    return () => {
      cancelled = true;
    };
  }, [authGet, invitedFrom, invitedTo]);

  const volumeBuckets = volume?.buckets ?? [];
  const preRate =
    volumeBuckets.find((b) => b.bucket === 'pre')?.msgsPerUserPerDay ?? null;
  const maxRate = Math.max(1, ...volumeBuckets.map((b) => b.msgsPerUserPerDay));
  // A rounded-up even axis top so the gridlines land on whole numbers.
  const axisTop = Math.max(2, Math.ceil(maxRate / 2) * 2);

  return (
    <div className='activation-dashboard'>
      {error && <div className='msg error'>{error}</div>}

      <div className='activation-controls'>
        <div className='day-selector'>
          {RANGE_DAYS.map((d) => (
            <button
              key={d}
              type='button'
              className={`day-btn ${rangeDays === d ? 'active' : ''}`}
              onClick={() => pickRange(d)}
            >
              {d} days
            </button>
          ))}
        </div>
        <label>
          Invited between
          <UkDateInput
            value={invitedFrom}
            max={invitedTo || undefined}
            onChange={(v) => {
              setRangeDays(null);
              setInvitedFrom(v);
            }}
          />
        </label>
        <label>
          and
          <UkDateInput
            value={invitedTo}
            min={invitedFrom || undefined}
            onChange={(v) => {
              setRangeDays(null);
              setInvitedTo(v);
            }}
          />
        </label>
        <div className='activation-control'>
          Source
          <TickBoxFilter
            groups={sourceGroups}
            groupLabels={SOURCE_GROUP_LABELS}
            selected={sources}
            onChange={changeSources}
            noun='sources'
          />
        </div>
        {codeGroups.length > 0 && (
          <div className='activation-control'>
            Code
            <TickBoxFilter
              groups={codeGroups}
              groupLabels={codeGroupLabels}
              selected={codes}
              onChange={setCodes}
              noun='codes'
            />
          </div>
        )}
        <label>
          Onboarding
          <select value={variant} onChange={(e) => setVariant(e.target.value)}>
            <option value=''>All variants</option>
            {(filters?.variants ?? []).map((o) => (
              <option key={o.key} value={o.key}>
                {o.label} ({o.count})
              </option>
            ))}
          </select>
        </label>
      </div>

      <OnboardingOverviewPanel
        authGet={authGet}
        cohortParams={cohortParams}
        invitedFrom={invitedFrom}
        variant={variant}
        cohortSummary={describeCohort(sources, codes, variant, filters)}
      />

      <SignupFunnelPanel />

      {/* ── 1b. Re-engagement (DRE-274) ── */}
      <section className='activation-section'>
        <h3>
          Drop-off re-engagement
          <span
            className='info-dot'
            title='WhatsApp nudges sent to users who stalled during onboarding: an invite nudge (and later a deferral offer) before the connect step, and two connect reminders after the connect link went out. Sends only happen 07:00–23:00 user-local time.'
          >
            ⓘ
          </span>
        </h3>
        <p className='activation-note'>
          Automated WhatsApp nudges for this cohort&apos;s stalled onboardings,
          and what happened after: replies, completions, deferrals (&quot;maybe
          later&quot;), stops (declined / STOP), &quot;I&apos;m stuck&quot;
          flags and recorded blockers.
        </p>
        {reengagement == null ? (
          <div className='loading'>Loading...</div>
        ) : (
          <>
            <div className='reengagement-flow'>
              <div className='reengagement-kpi'>
                <strong>{reengagement.nudged}</strong>
                <span>users nudged</span>
              </div>
              <span className='flow-arrow'>→</span>
              <div className='reengagement-kpi'>
                <strong>
                  {reengagement.repliedAfterNudge}
                  {reengagement.nudged > 0 &&
                    ` (${pctFmt(reengagement.repliedAfterNudge / reengagement.nudged)})`}
                </strong>
                <span>replied after a nudge</span>
              </div>
              <span className='flow-arrow'>→</span>
              <div className='reengagement-kpi'>
                <strong>
                  {reengagement.completedAfterNudge}
                  {reengagement.nudged > 0 &&
                    ` (${pctFmt(reengagement.completedAfterNudge / reengagement.nudged)})`}
                </strong>
                <span>completed onboarding after a nudge</span>
              </div>
            </div>
            <div className='reengagement-chips'>
              <button
                type='button'
                onClick={() => setReengagementUsersState('stuck')}
              >
                <strong>{reengagement.stuckFlagged}</strong> flagged “I&apos;m
                stuck”
              </button>
              <button
                type='button'
                onClick={() => setReengagementUsersState('deferred')}
              >
                <strong>{reengagement.states.deferred}</strong> paused (“maybe
                later”)
              </button>
              <button
                type='button'
                onClick={() => setReengagementUsersState('stopped')}
              >
                <strong>{reengagement.states.stopped}</strong> stopped
              </button>
            </div>
            <details className='activation-details'>
              <summary>Nudges sent, stop reasons and blockers</summary>
              <h4>Nudges sent</h4>
              <table className='activation-table'>
                <thead>
                  <tr>
                    <th>Nudge</th>
                    <th>When</th>
                    <th className='num'>Sent</th>
                  </tr>
                </thead>
                <tbody>
                  {NUDGE_LABELS.map(([key, label, when]) => (
                    <tr key={key}>
                      <td>{label}</td>
                      <td>{when}</td>
                      <td className='num'>{reengagement.nudges[key]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {reengagement.stoppedReasons.length > 0 && (
                <>
                  <h4>Stop reasons</h4>
                  <table className='activation-table'>
                    <thead>
                      <tr>
                        <th>Reason</th>
                        <th className='num'>Users</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reengagement.stoppedReasons.map((r) => (
                        <tr key={r.reason}>
                          <td>{r.reason}</td>
                          <td className='num'>{r.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
              {reengagement.blockers.length > 0 && (
                <>
                  <h4>Recorded blockers</h4>
                  <table className='activation-table'>
                    <thead>
                      <tr>
                        <th>Kind</th>
                        <th>Category</th>
                        <th>Provider</th>
                        <th className='num'>Users</th>
                        <th className='num'>OK to re-contact</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reengagement.blockers.map((b, i) => (
                        <tr key={`${b.kind}-${b.category}-${b.provider}-${i}`}>
                          <td>{b.kind}</td>
                          <td>{b.category ?? '—'}</td>
                          <td>{b.provider ?? '—'}</td>
                          <td className='num'>{b.count}</td>
                          <td className='num'>{b.consentToRecontact}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </details>
          </>
        )}
      </section>

      {/* ── 1c. Activation messages (Day 1/2/4) ── */}
      <section className='activation-section'>
        <h3>Activation messages</h3>
        <p className='activation-note'>
          First send of each follow-up to people who finished onboarding in the
          date range, and whether they engaged within 24 hours. “Briefing only”
          is people who got that day&apos;s briefing without the follow-up — a
          comparison, not a controlled test.
        </p>
        {touchSummary == null ? (
          <div className='loading'>Loading...</div>
        ) : (
          <table className='activation-table'>
            <thead>
              <tr>
                <th>Message</th>
                <th className='num'>Sent</th>
                <th className='num'>Replied ≤ 24h</th>
                <th className='num'>Briefing only: replied ≤ 24h</th>
                <th className='num'>Delegated a task ≤ 24h</th>
              </tr>
            </thead>
            <tbody>
              {touchSummary.map(([d, stats]) => {
                const t = stats.engagement.touched;
                const u = stats.engagement.untouched;
                const rate = (n: number, of: number) =>
                  of > 0 ? `${n} (${pctFmt(n / of)})` : '—';
                return (
                  <tr key={d}>
                    <td>
                      Day {d} — {activationTouchConfig(d).name}
                    </td>
                    <td className='num'>
                      {stats.days.reduce((n, day) => n + day.sent, 0)}
                    </td>
                    <td className='num'>
                      {t ? rate(t.replied24h, t.users) : '—'}
                    </td>
                    <td className='num'>
                      {u ? rate(u.replied24h, u.users) : '—'}
                    </td>
                    <td className='num'>
                      {t ? rate(t.taskWithin24h, t.users) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <details className='activation-details'>
          <summary>Day-by-day detail and reply analysis</summary>
          <h4>
            Day {touchDay} {touchConfig.name}
            <span className='info-dot' title={touchConfig.tooltip}>
              ⓘ
            </span>
          </h4>
          <div className='activation-touch-tabs'>
            {ACTIVATION_TOUCH_DAYS.map((d) => (
              <button
                key={d}
                type='button'
                className={`day-btn ${touchDay === d ? 'active' : ''}`}
                onClick={() => setTouchDay(d)}
              >
                Day {d} — {activationTouchConfig(d).name}
              </button>
            ))}
          </div>
          <p className='activation-note'>
            Users who completed onboarding in the selected date range: did the
            Day {touchDay} briefing go out, did the {touchConfig.name} follow,
            which variant was used, and how people reacted. Engagement compares
            recipients with briefing-only users on the same windows —
            descriptive lift, not a randomised control.
          </p>
          <div className='activation-touch-tabs touch-send-tabs'>
            {TOUCH_SEND_FILTERS.map((f) => (
              <button
                key={f.key}
                type='button'
                className={`day-btn ${touchSends === f.key ? 'active' : ''}`}
                onClick={() => setTouchSends(f.key)}
              >
                {f.label}
              </button>
            ))}
            <span className='activation-note'>
              {touchSends === 'first'
                ? 'Each user’s first successful send of this touch.'
                : touchSends === 'repeat'
                  ? 'Later sends only — the recurring form of this touch. One row per send, so a user can count more than once.'
                  : 'First and repeat sends together. One row per send, so a user can count more than once.'}
            </span>
          </div>
          {touchStatsError ? (
            <p className='activation-note sentiment-error'>{touchStatsError}</p>
          ) : touchStats == null ? (
            <div className='loading'>Loading...</div>
          ) : touchStats.days.length === 0 ? (
            <p className='activation-note'>
              No completed onboardings in this date range yet.
            </p>
          ) : (
            <>
              <div className='reengagement-kpis'>
                <div className='reengagement-kpi'>
                  <strong>
                    {touchStats.days.reduce((n, d) => n + d.completed, 0)}
                  </strong>
                  <span>completed onboarding</span>
                </div>
                {touchStats.days.some((d) => d.reachedDay != null) && (
                  <div className='reengagement-kpi'>
                    <strong>
                      {touchStats.days.reduce(
                        (n, d) => n + (d.reachedDay ?? 0),
                        0,
                      )}
                    </strong>
                    <span>reached Day {touchDay}</span>
                  </div>
                )}
                <div className='reengagement-kpi'>
                  <strong>
                    {touchStats.days.reduce(
                      (n, d) => n + d.briefingDelivered,
                      0,
                    )}
                  </strong>
                  <span>{touchConfig.briefingLabel}</span>
                </div>
                <div className='reengagement-kpi'>
                  <strong>
                    {touchStats.days.reduce((n, d) => n + d.sent, 0)}
                  </strong>
                  <span>{touchConfig.sentLabel}</span>
                </div>
                {touchStats.sends !== 'first' && (
                  <div className='reengagement-kpi'>
                    <strong>
                      {touchStats.days.reduce((n, d) => n + d.sendCount, 0)}
                    </strong>
                    <span>
                      {touchStats.sends === 'repeat'
                        ? 'repeat sends'
                        : 'sends in total'}
                    </span>
                  </div>
                )}
                {touchStats.options.map((o) => (
                  <div key={o.option} className='reengagement-kpi'>
                    <strong>{o.count}</strong>
                    <span>
                      {touchOptionName(touchDay, o.option)}{' '}
                      {touchConfig.optionSuffix}
                    </span>
                  </div>
                ))}
              </div>
              {touchStats.outcomes.length > 0 && (
                <>
                  <h4>Attempt outcomes</h4>
                  <p className='activation-note'>
                    Every attempt logged in the selected date range, by attempt
                    date — so it includes skips for users outside the completion
                    cohort, and sends to this cohort that fired after the range
                    ends are not listed here.
                  </p>
                  <table className='activation-table'>
                    <thead>
                      <tr>
                        <th>Outcome</th>
                        <th className='num'>Attempts</th>
                      </tr>
                    </thead>
                    <tbody>
                      {touchStats.outcomes.map((o) => (
                        <tr key={o.outcome}>
                          <td>
                            {TOUCH_OUTCOME_LABELS[o.outcome] ?? o.outcome}
                          </td>
                          <td className='num'>{o.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
              {(touchStats.engagement.touched ||
                touchStats.engagement.untouched) && (
                <>
                  <h4>Engagement after the touch</h4>
                  <table className='activation-table'>
                    <thead>
                      <tr>
                        <th>Cohort</th>
                        <th className='num'>
                          {touchStats.sends === 'first' ? 'Users' : 'Sends'}
                        </th>
                        <th className='num'>Replied ≤ 1h</th>
                        <th className='num'>Replied ≤ 24h</th>
                        <th className='num'>Delegated a task ≤ 24h</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(
                        [
                          [
                            touchConfig.recipientsLabel,
                            touchStats.engagement.touched,
                          ],
                          ['Briefing only', touchStats.engagement.untouched],
                        ] as Array<[string, TouchEngagementCohort | null]>
                      ).map(
                        ([label, c]) =>
                          c && (
                            <tr key={label}>
                              <td>{label}</td>
                              <td className='num'>{c.users}</td>
                              <td className='num'>
                                {c.replied1h}
                                {c.users > 0 &&
                                  ` (${pctFmt(c.replied1h / c.users)})`}
                              </td>
                              <td className='num'>
                                {c.replied24h}
                                {c.users > 0 &&
                                  ` (${pctFmt(c.replied24h / c.users)})`}
                              </td>
                              <td className='num'>
                                {c.taskWithin24h}
                                {c.users > 0 &&
                                  ` (${pctFmt(c.taskWithin24h / c.users)})`}
                              </td>
                            </tr>
                          ),
                      )}
                    </tbody>
                  </table>
                </>
              )}
              <h4>How did people react?</h4>
              <p className='activation-note'>
                Draws a sample of the touch&apos;s recipients in this date range
                (stratified by option so every variant is represented), runs an
                LLM pass over each one&apos;s replies in the 24h after the
                touch, and flags harmful and successful exchanges. Everyone
                outside the sample is also screened for harm signals (opt-outs,
                privacy alarm, anger, distress), so harmful interactions are
                surfaced from the whole cohort. Costs a model call per replier,
                so pick a sample size.
              </p>
              <div className='sentiment-controls'>
                <label>
                  Sampling
                  <select
                    value={sentimentSampling}
                    onChange={(e) =>
                      setSentimentSampling(e.target.value as TouchSampling)
                    }
                    disabled={sentimentLoading}
                  >
                    {SAMPLING_MODES.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Sample size
                  <select
                    value={sentimentLimit}
                    onChange={(e) => setSentimentLimit(Number(e.target.value))}
                    disabled={sentimentLoading}
                  >
                    {SENTIMENT_LIMITS.map((n) => (
                      <option key={n} value={n}>
                        up to {n} recipients
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type='button'
                  className='sentiment-run-btn'
                  onClick={runSentiment}
                  disabled={sentimentLoading}
                >
                  {sentimentLoading
                    ? describeSentimentProgress(sentimentProgress)
                    : sentiment
                      ? 'Re-run analysis'
                      : 'Analyse replies'}
                </button>
                {sentimentError && (
                  <span className='sentiment-error'>{sentimentError}</span>
                )}
              </div>
              {sentiment && (
                <div className='sentiment-results'>
                  {sentiment.cohort > 0 && (
                    <>
                      <p className='sentiment-coverage'>
                        <strong>
                          Sampled {sentiment.totals.recipients} of{' '}
                          {sentiment.cohort}
                        </strong>{' '}
                        cohort recipients (
                        {sentiment.sampling === 'random'
                          ? 'random, stratified by option'
                          : 'most recent, stratified by option'}
                        )
                        {sentiment.strata.length > 1 && (
                          <>
                            {' — '}
                            {sentiment.strata
                              .map(
                                (s) =>
                                  `${
                                    s.option != null
                                      ? touchOptionName(
                                          sentiment.dayIndex,
                                          s.option,
                                        )
                                      : 'No option'
                                  } ${s.sampled}/${s.cohort}`,
                              )
                              .join(', ')}
                          </>
                        )}
                        . Counts below describe the sample, not the whole
                        cohort.
                      </p>
                      <div className='reengagement-kpis'>
                        <div className='reengagement-kpi'>
                          <strong>{sentiment.totals.recipients}</strong>
                          <span>recipients in sample</span>
                        </div>
                        <div className='reengagement-kpi'>
                          <strong>{sentiment.totals.analysed}</strong>
                          <span>replied within 24h</span>
                        </div>
                        <div className='reengagement-kpi'>
                          <strong>{sentiment.totals.noReply}</strong>
                          <span>no reply</span>
                        </div>
                        <div className='reengagement-kpi sentiment-kpi--positive'>
                          <strong>{sentiment.totals.positive}</strong>
                          <span>positive</span>
                        </div>
                        <div className='reengagement-kpi'>
                          <strong>{sentiment.totals.neutral}</strong>
                          <span>neutral</span>
                        </div>
                        <div className='reengagement-kpi sentiment-kpi--negative'>
                          <strong>{sentiment.totals.negative}</strong>
                          <span>negative</span>
                        </div>
                        <div className='reengagement-kpi sentiment-kpi--success'>
                          <strong>{sentiment.totals.successful}</strong>
                          <span>successful — user leaned in</span>
                        </div>
                        <div className='reengagement-kpi sentiment-kpi--harm'>
                          <strong>
                            {sentiment.totals.harmful -
                              sentiment.totals.dangerous}
                          </strong>
                          <span>annoying (in sample)</span>
                        </div>
                        <div className='reengagement-kpi sentiment-kpi--danger'>
                          <strong>{sentiment.totals.dangerous}</strong>
                          <span>dangerous (in sample)</span>
                        </div>
                      </div>
                      <p
                        className={`sentiment-harm-scan${
                          sentiment.harmScan.confirmed > 0
                            ? ' sentiment-harm-scan--hit'
                            : ''
                        }`}
                      >
                        <strong>Cohort harm scan:</strong>{' '}
                        {sentiment.harmScan.scanned} recipient
                        {sentiment.harmScan.scanned === 1 ? '' : 's'} outside
                        the sample screened for harm signals ·{' '}
                        {sentiment.harmScan.flagged} flagged for review ·{' '}
                        {sentiment.harmScan.confirmed} confirmed harmful (
                        {sentiment.harmScan.confirmedDangerous} dangerous)
                        {sentiment.harmScan.unscanned > 0 &&
                          ` · ${sentiment.harmScan.unscanned} not scanned (thread unavailable or over the scan cap)`}
                        . Confirmed cases are listed below.
                      </p>
                    </>
                  )}
                  {sentiment.totals.unavailable > 0 && (
                    <p className='activation-note'>
                      {sentiment.totals.unavailable} recipient
                      {sentiment.totals.unavailable === 1 ? '' : 's'} skipped
                      (thread history unavailable or analysis failed).
                    </p>
                  )}
                  {sentiment.totals.analysed === 0 &&
                  sentiment.harmScan.confirmed === 0 ? (
                    <p className='activation-note'>
                      {sentiment.cohort === 0
                        ? `No Day ${sentiment.dayIndex} touches have been sent to this cohort yet.`
                        : 'Nobody in this sample replied within 24h, so there is nothing to classify.'}
                    </p>
                  ) : (
                    <ul className='sentiment-list'>
                      {sentiment.items
                        .filter((it) => it.analysis)
                        .map((it) => {
                          const a = it.analysis!;
                          return (
                            <li
                              key={it.userId}
                              className={`sentiment-item sentiment-item--${
                                a.harmSeverity === 'dangerous'
                                  ? 'danger'
                                  : a.harmful
                                    ? 'harm'
                                    : a.successful
                                      ? 'success'
                                      : a.sentiment
                              }`}
                            >
                              <div className='sentiment-item-head'>
                                {it.source === 'harm_scan' && (
                                  <span className='sentiment-tag sentiment-tag--scan'>
                                    Found by cohort scan
                                  </span>
                                )}
                                {a.harmful &&
                                  (a.harmSeverity === 'dangerous' ? (
                                    <span className='sentiment-tag sentiment-tag--danger'>
                                      Dangerous
                                    </span>
                                  ) : (
                                    <span className='sentiment-tag sentiment-tag--harm'>
                                      Annoying
                                    </span>
                                  ))}
                                {a.successful && (
                                  <span className='sentiment-tag sentiment-tag--success'>
                                    Successful
                                  </span>
                                )}
                                <span
                                  className={`sentiment-tag sentiment-tag--${a.sentiment}`}
                                >
                                  {a.sentiment}
                                </span>
                                <span className='sentiment-meta'>
                                  {it.option != null &&
                                    `${touchOptionName(it.dayIndex, it.option)} · `}
                                  sent {fmtShortDate(it.sentAt)} ·{' '}
                                  {it.replyCount}{' '}
                                  {it.replyCount === 1 ? 'reply' : 'replies'}
                                </span>
                              </div>
                              <p className='sentiment-summary'>
                                {TOUCH_REACTION_LABELS[a.reaction] ??
                                  a.reaction}
                              </p>
                              {a.harmful && a.harmCategory && (
                                <p className='sentiment-harm-reason'>
                                  {TOUCH_HARM_LABELS[a.harmCategory] ??
                                    a.harmCategory}
                                </p>
                              )}
                            </li>
                          );
                        })}
                    </ul>
                  )}
                </div>
              )}
            </>
          )}
        </details>
      </section>

      {/* ── 2. Message volume ── */}
      <section className='activation-section'>
        <h3>
          Daily message volume per user — before and after completing onboarding
          <span
            className='info-dot'
            title='From user_messages: inbound user messages only, per user per calendar day. Days with zero messages count in the denominator. secretary turns are not counted.'
          >
            ⓘ
          </span>
        </h3>
        <p className='activation-note'>
          Average inbound messages per user per day. Before completing is every
          day from invite up to finishing onboarding; after that it is bucketed
          by days since they finished. Users only appear in a bucket once they
          are old enough to have lived through it, so each bar has its own n.
        </p>
        {volume == null ? (
          <div className='loading'>Loading...</div>
        ) : volumeBuckets.length === 0 ? (
          <p className='activation-note'>
            Nobody in this view has completed onboarding yet.
          </p>
        ) : (
          <>
            <div className='volume-chart'>
              <div className='volume-yaxis'>
                {[0, 1, 2, 3, 4].map((i) => (
                  <span key={i} style={{ top: `${i * 25}%` }}>
                    {Math.round(axisTop - (i * axisTop) / 4)}
                  </span>
                ))}
              </div>
              <div className='volume-plot'>
                {[0, 1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className='volume-grid'
                    style={{ top: `${i * 25}%` }}
                  />
                ))}
                {volumeBuckets.map((b, i) => (
                  <React.Fragment key={b.bucket}>
                    {i === 1 && (
                      <div className='volume-divider'>
                        <em>completed onboarding</em>
                      </div>
                    )}
                    <div className='volume-col'>
                      <div
                        className={`volume-bar ${b.bucket === 'pre' ? 'pre' : ''}`}
                        style={{
                          height: `${Math.min(
                            (b.msgsPerUserPerDay / axisTop) * 100,
                            100,
                          )}%`,
                        }}
                      >
                        <span className='volume-val'>
                          {b.msgsPerUserPerDay.toFixed(1)}
                        </span>
                      </div>
                    </div>
                  </React.Fragment>
                ))}
              </div>
              <div className='volume-xlabels'>
                {volumeBuckets.map((b) => (
                  <div key={b.bucket}>
                    {VOLUME_BUCKET_LABELS[b.bucket].label}
                    <small>{VOLUME_BUCKET_LABELS[b.bucket].sub}</small>
                  </div>
                ))}
              </div>
            </div>

            <table className='activation-table'>
              <thead>
                <tr>
                  <th>Window</th>
                  <th className='num'>Users</th>
                  <th className='num'>User-days</th>
                  <th className='num'>Messages</th>
                  <th className='num'>Msgs / user / day</th>
                  <th className='num'>Active days per user</th>
                  <th className='num'>vs before completing</th>
                </tr>
              </thead>
              <tbody>
                {volumeBuckets.map((b) => (
                  <tr key={b.bucket}>
                    <td>{VOLUME_BUCKET_LABELS[b.bucket].tableLabel}</td>
                    <td className='num'>{b.users}</td>
                    <td className='num'>{b.userDays.toLocaleString()}</td>
                    <td className='num'>{b.messages.toLocaleString()}</td>
                    <td className='num'>
                      <strong>{b.msgsPerUserPerDay.toFixed(1)}</strong>
                    </td>
                    <td className='num'>{pctFmt(b.activeDayShare)}</td>
                    <td
                      className={`num ${
                        b.bucket !== 'pre' && preRate ? 'positive' : ''
                      }`}
                    >
                      {b.bucket === 'pre' || !preRate
                        ? '—'
                        : `${b.msgsPerUserPerDay >= preRate ? '+' : ''}${(
                            b.msgsPerUserPerDay / preRate
                          ).toFixed(1)}×`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </section>

      {reengagementUsersState && (
        <div
          className='reengagement-modal-overlay'
          onClick={() => setReengagementUsersState(null)}
        >
          <div
            className='reengagement-modal'
            onClick={(e) => e.stopPropagation()}
          >
            <div className='reengagement-modal-header'>
              <h4>
                {reengagementUsersState === 'stuck'
                  ? 'Users flagged “I’m stuck”'
                  : reengagementUsersState === 'deferred'
                    ? 'Paused users (“maybe later”)'
                    : 'Stopped users (never re-contact)'}
              </h4>
              <button
                type='button'
                className='close-btn'
                onClick={() => setReengagementUsersState(null)}
              >
                ×
              </button>
            </div>
            {reengagementUsersLoading ? (
              <div className='loading'>Loading...</div>
            ) : reengagementUsers.length === 0 ? (
              <p className='reengagement-modal-empty'>No users found.</p>
            ) : (
              <ul className='reengagement-user-list'>
                {reengagementUsers.map((u) => (
                  <li key={u.id}>
                    <span className='reengagement-user-name'>
                      {u.name || u.id}
                    </span>
                    {u.companyName && (
                      <span className='reengagement-user-company'>
                        {' '}
                        · {u.companyName}
                      </span>
                    )}
                    {u.stoppedReason && (
                      <span className='reengagement-user-reason'>
                        {' '}
                        — {u.stoppedReason}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivationDashboard;
